import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  Typography,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  useMediaQuery,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const validationSchema = Yup.object({
  field_label: Yup.string().required("Field label is required"),
  field_type: Yup.string().required("Field type is required"),
  location: Yup.string().required("Location is required"),
  parent_field_id: Yup.string().when("field_type", {
    is: "option",
    then: (schema) => schema.required("Parent field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const LOCATION_OPTIONS = [
  { value: "create", label: "Create" },
  { value: "sign", label: "Sign" },
];

const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "selectGroup", label: "Select Group" },
  { value: "option", label: "Option" },
  { value: "checkboxGroup", label: "Checkbox Group" },
];

const NewFieldDialog = ({
  open,
  handleClose,
  getFields,
  selectedField,
  fields,
  filter,
  searchField,
  setPage,
}) => {
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const [createFieldMessage, setCreateFieldMessage] = useState({
    type: "",
    message: "",
  });

  const formik = useFormik({
    initialValues: {
      field_label: selectedField?.field_label ?? "",
      field_type: selectedField?.field_type ?? "",
      location: selectedField?.location ?? "",
      parent_field_id: selectedField?.parent_field_id ?? "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setCreateFieldMessage({ type: "", message: "" });
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/upsert`,
          {
            currentSession: currentSession,
            field: {
              ...values,
              ...(selectedField ? { field_id: selectedField?.field_id } : {}),
            },
          },
          {
            withCredentials: true,
          },
        );

        if (response?.data?.success) {
          setCreateFieldMessage({
            type: "success",
            message:
              response?.data?.message ||
              `Form field ${selectedField ? "updated" : "created"} successfully`,
          });
          resetForm();
          getFields(filter, 1, searchField);
          setPage(1);
          handleClose();
        }
      } catch (error) {
        setCreateFieldMessage({
          type: "error",
          message:
            error?.response?.data?.message ||
            `An error occurred while ${selectedField ? "updating" : "creating"} field`,
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    resetForm,
  } = formik;

  const parentFields = useMemo(
    () =>
      fields
        ?.filter(
          (field) =>
            field?.field_type === "selectGroup" ||
            field?.field_type === "checkboxGroup",
        )
        .map((field) => ({
          label: field?.field_label,
          value: field?.field_id,
        })),
    [fields],
  );

  const handleCloseModal = () => {
    resetForm();
    setCreateFieldMessage({ type: "", message: "" });
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: { ...(mobileSize ? { margin: 0, width: "95%" } : {}) },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            {selectedField ? "Update" : "Create"} Form field
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleCloseModal}
          disabled={isSubmitting}
          sx={{ mt: -0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />

      <FormikProvider value={formik}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <DialogContent sx={{ px: 3, py: 2.5 }}>
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Field Label"
                  name="field_label"
                  value={values.field_label}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.field_label && Boolean(errors.field_label)}
                  helperText={touched.field_label && errors.field_label}
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Select Field Type"
                  name="field_type"
                  value={values.field_type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.field_type && Boolean(errors.field_type)}
                  helperText={touched.field_type && errors.field_type}
                >
                  {FIELD_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Select Location"
                  name="location"
                  value={values.location}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.location && Boolean(errors.location)}
                  helperText={touched.location && errors.location}
                >
                  {LOCATION_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              {values?.field_type === "option" && (
                <Stack direction="row" spacing={2}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Select Parent Field"
                    name="parent_field_id"
                    value={values.parent_field_id}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.parent_field_id && Boolean(errors.parent_field_id)
                    }
                    helperText={
                      touched.parent_field_id && errors.parent_field_id
                    }
                  >
                    {parentFields.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              )}

              {createFieldMessage?.message && (
                <Alert
                  severity={createFieldMessage?.type}
                  sx={{ borderRadius: 2 }}
                >
                  {createFieldMessage?.message}
                </Alert>
              )}
            </Stack>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              onClick={handleCloseModal}
              variant="text"
              disabled={isSubmitting}
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null
              }
              sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
            >
              {isSubmitting ? "Saving…" : "Save Field"}
            </Button>
          </DialogActions>
        </Box>
      </FormikProvider>
    </Dialog>
  );
};

export default NewFieldDialog;
